import React, { useMemo } from 'react';
import { formatNumber, formatDifference, formatRank, formatPercentile } from '../../utils/formatting';
import { getValueColor } from '../../utils/coloring';
import { getCrimeDisplayName } from '../../constants/crimes';
import { getStateAbbreviation } from '../../constants/locations';
import './Tables.css';

const HistoricalTotalsTable = ({ 
  selectedLocation, 
  selectedCrime, 
  selectedDateRange,
  setSelectedDateRange,
  setIsHistorical,
  selectedSource,
  compareMode
}) => {  
  const { info, data } = selectedLocation;
  const locationType = info?.type?.charAt(0).toUpperCase() + info?.type?.slice(1);
  const isNation = info?.type?.toLowerCase() === 'nation';
  const isCounty = info?.type?.toLowerCase() === 'county';
  const isCity = info?.type?.toLowerCase() === 'city';
  const isCountyOrCity = isCounty || isCity;
  
  const stateAbbr = useMemo(() => {
    if (isCountyOrCity && info?.name?.includes(',')) {
      const stateName = info.name.split(',')[1]?.trim();
      return getStateAbbreviation(stateName);
    }
    return getStateAbbreviation(selectedLocation);
  }, [isCountyOrCity, info?.name, selectedLocation]);

  const handleYearClick = (year) => {
    setSelectedDateRange(year.toString());
    setTimeout(() => {
      setIsHistorical(false);
    }, 100);
  };

  const transformData = () => {
    if (!data?.historical) {
      return [];
    }

    return data.historical
      .sort((a, b) => b.year - a.year)
      .map(yearData => {
        const crimeField = `${selectedCrime.toLowerCase()}_count`;
        const total = yearData[crimeField];
        
        if (total === null || total === undefined) return null;

        const rankField = isCountyOrCity
          ? compareMode === 'state'
            ? `${selectedCrime.toLowerCase()}_state_count_rank`
            : `${selectedCrime.toLowerCase()}_national_count_rank`
          : `${selectedCrime.toLowerCase()}_count_rank`;
        
        const percentileField = isCountyOrCity
          ? compareMode === 'state'
            ? `${selectedCrime.toLowerCase()}_state_count_percentile`
            : `${selectedCrime.toLowerCase()}_national_count_percentile`
          : `${selectedCrime.toLowerCase()}_count_percentile`;

        return {
          year: yearData.year,
          total,
          nationalAvg: yearData[`${selectedCrime.toLowerCase()}_national_count_avg`],
          nationalDiff: yearData[`${selectedCrime.toLowerCase()}_national_count_avg_diff`],
          nationalDiffPct: yearData[`${selectedCrime.toLowerCase()}_national_count_avg_diff_pct`],
          stateAvg: isCountyOrCity ? yearData[`${selectedCrime.toLowerCase()}_state_count_avg`] : null,
          stateDiff: isCountyOrCity ? yearData[`${selectedCrime.toLowerCase()}_state_count_avg_diff`] : null,
          stateDiffPct: isCountyOrCity ? yearData[`${selectedCrime.toLowerCase()}_state_count_avg_diff_pct`] : null,
          rank: yearData[rankField],
          percentile: yearData[percentileField],
          totalCountiesState: isCounty ? yearData.total_counties_state : null,
          totalCitiesState: isCity ? yearData.total_cities_state : null,
          totalCountiesNational: isCounty ? yearData.total_counties_national : null,
          totalCitiesNational: isCity ? yearData.total_cities_national : null,
          totalLocations: yearData.total_locations
        };
      })
      .filter(row => row !== null);
  };

  const tableData = transformData();

  if (!tableData.length) {
    return <div className="loading">No Historical Data Available</div>;
  }

  return (
    <div className="main-table-wrapper">
      <div className="main-table-top">
        {selectedCrime === 'all_crime' ? 'All' : ''}&nbsp;{getCrimeDisplayName(selectedCrime, selectedSource)}&nbsp;Historical Totals
      </div>
      <div className="main-table-scroll-container">
        <table className={`main-table ${isNation ? 'nation-view' : ''} totals-table historical-table`}>
          <thead>
            <tr>
              <th>Year</th>
              <th>
                {isNation ? (
                  <div className="single-line-header">Total {getCrimeDisplayName(selectedCrime, selectedSource)}</div>
                ) : (
                  <div className="stacked-header">
                    <div>Total</div>
                    <div>{getCrimeDisplayName(selectedCrime, selectedSource)}</div>
                  </div>
                )}
              </th>
              {!isNation && (
                <>
                  <th>
                    <div className="stacked-header">
                      <div>{isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationType}</div>
                      <div>Average</div>
                    </div>
                  </th>
                  <th>
                    <div className="stacked-header">
                      <div>{isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationType} Avg</div>
                      <div>Difference</div>
                    </div>
                  </th>
                  <th>
                    <div className="stacked-header">
                      <div>% {isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationType} Avg</div>
                      <div>Difference</div>
                    </div>
                  </th>
                  <th>
                    <div className="stacked-header">
                      <div>{isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationType}</div>
                      <div>Rank</div>
                    </div>
                  </th>
                  <th>
                    <div className="stacked-header">
                      <div>{isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationType}</div>
                      <div>Percentile</div>
                    </div>
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {tableData.map((row) => {
              const avgValue = compareMode === 'state' && isCountyOrCity && row.stateAvg !== null 
                ? row.stateAvg 
                : row.nationalAvg;
                
              const diffValue = compareMode === 'state' && isCountyOrCity && row.stateDiff !== null 
                ? row.stateDiff 
                : row.nationalDiff;
                
              const diffPctValue = compareMode === 'state' && isCountyOrCity && row.stateDiffPct !== null 
                ? row.stateDiffPct 
                : row.nationalDiffPct;

              return (
                <tr 
                  key={row.year} 
                  className={row.year === parseInt(selectedDateRange) ? 'selected-row' : ''}
                >
                  <td>
                    <span onClick={() => handleYearClick(row.year)}>
                      {row.year}
                    </span>
                  </td>
                  <td className={`number-cell ${isNation ? 'nation' : ''} bold`}>
                    {formatNumber(row.total)}
                  </td>
                  {!isNation && (
                    <>
                      <td className="number-cell">{formatNumber(avgValue)}</td>
                      <td 
                        className="number-cell" 
                        style={{ color: getValueColor(diffValue) }}
                      >
                        {formatDifference(diffValue)}
                      </td>
                      <td 
                        className="number-cell" 
                        style={{ color: getValueColor(diffPctValue) }}
                      >
                        {formatDifference(diffPctValue, true)}
                      </td>
                      <td className="rank-cell">{formatRank(row.rank)}</td>
                      <td className="rank-cell">{formatPercentile(row.percentile)}</td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HistoricalTotalsTable;